import { Platform } from 'src/app/models/api';
import { BaseViolationRule } from 'src/app/models/api/governance/violationRule.model';
import { owaspLcncToDisplayName } from 'src/app/common/violationRulesUtils';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import {
  EffectivePolicySettingEnum,
  CategoryToRules,
  RuleSummary,
} from 'src/app/services/infra/store/model/effectivePolicySetting.model';

export class EffectivePolicySettingsStore {
  public _platformToEffectivePolicySettingsToCategoryRules: Map<
    Platform,
    Map<EffectivePolicySettingEnum, CategoryToRules[]>
  >;

  constructor(
    private readonly _rules: BaseViolationRule[],
    private readonly _logger: LoggerService
  ) {
    this._rulesData(_rules);
  }

  public getCategoryRulesByPlatformAndSettings(
    platform: Platform,
    effectivePolicySetting: EffectivePolicySettingEnum
  ): CategoryToRules[] {
    return this._platformToEffectivePolicySettingsToCategoryRules.get(platform)?.get(effectivePolicySetting) ?? [];
  }

  private _rulesData(rules: BaseViolationRule[]) {
    this._platformToEffectivePolicySettingsToCategoryRules = new Map<
      Platform,
      Map<EffectivePolicySettingEnum, CategoryToRules[]>
    >();

    for (const platform in Platform) {
      const platformEnum = Platform[platform as keyof typeof Platform];
      const effectivePolicySettingsToCategoryToRules = new Map<EffectivePolicySettingEnum, CategoryToRules[]>();
      rules
        .filter((rule) => rule.platform == platformEnum)
        .forEach((rule) => {
          this._setEffectivePolicySettingsToCategoryToRules(rule, effectivePolicySettingsToCategoryToRules);
        });
      this._platformToEffectivePolicySettingsToCategoryRules.set(
        platformEnum,
        effectivePolicySettingsToCategoryToRules
      );
    }
  }

  private _setEffectivePolicySettingsToCategoryToRules(
    rule: BaseViolationRule,
    effectivePolicySettingsToCategoryToRules: Map<EffectivePolicySettingEnum, CategoryToRules[]>
  ) {
    if (!rule.effectivePolicySettings || !Array.isArray(rule.effectivePolicySettings)) {
      return;
    }
    rule.effectivePolicySettings.forEach((effectivePolicySetting) => {
      const effectivePolicySettingEnum = this._mapEffectivePolicySettingNameToKey(effectivePolicySetting);
      if (effectivePolicySettingEnum == undefined) {
        this._logger.error('effective policy setting name not found: ' + effectivePolicySetting);
        return;
      }
      if (effectivePolicySettingsToCategoryToRules.get(effectivePolicySettingEnum) === undefined) {
        effectivePolicySettingsToCategoryToRules.set(effectivePolicySettingEnum, new Array<CategoryToRules>());
      }
      this._addCategoryToRules(rule, effectivePolicySettingsToCategoryToRules.get(effectivePolicySettingEnum));
    });
  }

  private _addCategoryToRules(rule: BaseViolationRule, categoriesToRules: CategoryToRules[] | undefined) {
    if (categoriesToRules == null) {
      this._logger.error('effective policy setting name not found in map: ');
      return;
    }

    for (const owasp of rule.owasp) {
      const categoryDisplayName = owaspLcncToDisplayName[owasp];

      if (
        categoryDisplayName != null &&
        categoriesToRules.every((categoryToRules) => categoryToRules.category != categoryDisplayName)
      ) {
        categoriesToRules.push(new CategoryToRules(categoryDisplayName, owasp, []));
      }
      if (categoryDisplayName != null) {
        for (const categoryToRules of categoriesToRules) {
          if (categoryToRules.category === categoryDisplayName) {
            categoryToRules.rulesSummary.push(new RuleSummary(rule.id, rule.displayName));
          }
        }
      }
    }
  }

  private _mapEffectivePolicySettingNameToKey(
    effectivePolicySettingName: string
  ): EffectivePolicySettingEnum | undefined {
    switch (effectivePolicySettingName) {
      case 'allowed_num_of_builders':
        return EffectivePolicySettingEnum.allowedNumOfBuilders;
      case 'allow_connections_to_prod_envs':
        return EffectivePolicySettingEnum.allowedConnectionsToProd;
      case 'num_of_high_privileged_users_in_environment':
        return EffectivePolicySettingEnum.highNumberOfPrivilegedUsers;
      case 'allow_connections_to_non_prod_envs':
        return EffectivePolicySettingEnum.allowedConnectionsToNonProd;
      case 'num_of_critical_privileged_users_in_environment':
        return EffectivePolicySettingEnum.criticalNumberOfPrivilegedUsers;
      case 'allowed_num_of_privileged_identities_in_workspace':
        return EffectivePolicySettingEnum.allowedNumOfPrivilegedIdentitiesInWorkspace;
      case 'allowed_num_of_privileged_identities_to_dashboard':
        return EffectivePolicySettingEnum.allowedNumOfPrivilegedIdentitiesToDashboard;
      case 'allowed_num_of_privileged_identities_to_dataset':
        return EffectivePolicySettingEnum.allowedNumOfPrivilegedIdentitiesToDataset;
      case 'allowed_num_of_privileged_identities_to_report':
        return EffectivePolicySettingEnum.allowedNumOfPrivilegedIdentitiesToReport;
      case 'allowed_num_of_privileged_identities_to_agent':
        return EffectivePolicySettingEnum.allowedNumOfPrivilegedIdentitiesToAgent;
      case 'allowed_num_of_privileged_identities_to_ai_skill':
        return EffectivePolicySettingEnum.allowedNumOfPrivilegedIdentitiesToAiSkill;
      case 'allowed_num_of_privileged_identities_to_gpt':
        return EffectivePolicySettingEnum.allowedNumOfPrivilegedIdentitiesToGpt;
      case 'allowed_num_of_privileged_identities_to_table':
        return EffectivePolicySettingEnum.allowedNumOfPrivilegedIdentitiesToTable;
      case 'num_of_days_to_retain_conversational_context':
      case 'allowed_memory_retention_in_days':
        return EffectivePolicySettingEnum.allowedMemoryRetentionInDays;
      case 'allowed_memory_max_recent_sessions':
        return EffectivePolicySettingEnum.allowedMemoryMaxRecentSessions;
      case 'allowed_identity_providers':
        return EffectivePolicySettingEnum.allowedIdentityProviders;
      case 'endpoints':
        return EffectivePolicySettingEnum.endpoints;
      case 'connector_list':
        return EffectivePolicySettingEnum.connectorList;
      case 'allowed_domains':
        return EffectivePolicySettingEnum.domainsList;
      case 'mcp_servers':
        return EffectivePolicySettingEnum.mcpServers;
      default:
        this._logger.error('effective policy setting name not found: ' + effectivePolicySettingName);
        return undefined;
    }
  }
}
